import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { useAccounting } from "@/contexts/AccountingContext";
import { useInventory } from "@/contexts/InventoryContext";
import { DuplicateEntryWarning } from "@/components/accounting/DuplicateEntryWarning";
import { ItemAutocomplete, SelectedItemPayload } from "@/components/accounting/ItemAutocomplete";
import { PartyAutocomplete } from "@/components/accounting/PartyAutocomplete";
import { InvoicePreviewDialog } from "@/components/accounting/InvoicePreviewDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useCompany } from "@/contexts/CompanyContext";
import { useClients } from "@/contexts/ClientContext";
import { useParties } from "@/contexts/PartyContext";
import { getAccountingPermissions, validatePeriodLock } from "@/lib/accounting/engine";
import { calculateGST, VoucherType, Voucher, VoucherLine, InvoiceItem } from "@/lib/accounting/types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FormModal } from "@/components/form/FormModal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus,
  Trash2,
  FileText,
  Zap,
  RefreshCw,
  AlertCircle,
  Wrench,
  Printer,
  Check,
  Wallet,
  CreditCard,
  Building2,
  Calendar,
  Lock,
  ChevronDown,
  Layers,
  ArrowRight,
  ShieldAlert,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { GSTINValidator } from "@/components/accounting/GSTINValidator";
import { diagnoseInvoicePrerequisites, DiagnosticReport } from "@/lib/accounting/diagnostics";
import { AccountingRepairDialog } from "./AccountingRepairDialog";

interface GSTInvoiceFormProps {
  type: VoucherType;
  trigger?: React.ReactNode;
  onSuccess?: () => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface InvoiceLine {
  id: string;
  itemId?: string;
  itemType?: "goods" | "service";
  description: string;
  hsnCode: string;
  sacCode?: string;
  qty: number;
  rate: number;
  discount: number; // Discount in ₹
  gstRate: number;
  amount: number; // Taxable line amount after discount
  unit?: string;
  isAutoCreated?: boolean;
}

type SettlementMode = "credit" | "full" | "partial";
type PaymentMethod = "Cash" | "Bank Account" | "UPI" | "Cheque" | "Card";

const GST_RATES = [0, 5, 12, 18, 28];
const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu & Kashmir", "Ladakh", "Puducherry", "Chandigarh",
];

export function GSTInvoiceForm({
  type,
  trigger,
  onSuccess,
  isOpen,
  onOpenChange,
}: GSTInvoiceFormProps) {
  const { ledgers, vouchers, stockItems, createVoucher, addStockItem, updateStockItem, refreshStockItems } = useAccounting();
  const { role, user } = useAuth();
  const { activeCompany } = useCompany();
  const { clients } = useClients();
  const { parties } = useParties();
  const { toast } = useToast();
  const perms = getAccountingPermissions(role, type);

  const [internalOpen, setInternalOpen] = useState(false);
  const open = isOpen !== undefined ? isOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;
  const [fastEntry, setFastEntry] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Preview dialog states
  const [previewOpen, setPreviewOpen] = useState(false);
  const [savedVoucherForPreview, setSavedVoucherForPreview] = useState<Voucher | null>(null);

  const formRef = useRef<HTMLDivElement>(null);
  const narrationRef = useRef<HTMLTextAreaElement>(null);
  const fieldRefs = useRef<Record<string, Record<string, HTMLElement | null>>>({});

  // Core Form State
  const [partyLedgerId, setPartyLedgerId] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [placeOfSupply, setPlaceOfSupply] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [narration, setNarration] = useState("");
  const [partyGstin, setPartyGstin] = useState("");
  const [, setGstinValidated] = useState(false);
  const [diagnosticReport, setDiagnosticReport] = useState<DiagnosticReport | null>(null);
  const [showRepairDialog, setShowRepairDialog] = useState(false);
  const [inlineRepairError, setInlineRepairError] = useState<string | null>(null);

  // Line items state
  const [items, setItems] = useState<InvoiceLine[]>([
    {
      id: crypto.randomUUID(),
      itemType: "goods",
      description: "",
      hsnCode: "",
      qty: 1,
      rate: 0,
      discount: 0,
      gstRate: 18,
      amount: 0,
      unit: "Nos",
    },
  ]);

  // Payment Settlement State
  const [settlementMode, setSettlementMode] = useState<SettlementMode>("credit");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("Bank Account");
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [paidAmount, setPaidAmount] = useState<number>(0);
  const [referenceNo, setReferenceNo] = useState("");
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().split("T")[0]);

  // Split payment toggle
  const [hasSplitPayment, setHasSplitPayment] = useState(false);
  const [splitMethod, setSplitMethod] = useState<PaymentMethod>("Cash");
  const [splitAccountId, setSplitAccountId] = useState("");
  const [splitAmount, setSplitAmount] = useState<number>(0);
  const [splitReferenceNo, setSplitReferenceNo] = useState("");

  const isCustomerType = type === "Sales" || type === "Credit Note";
  const companyState = activeCompany?.state || "Punjab";

  // Filter bank and cash ledgers for immediate settlement
  const bankAndCashLedgers = useMemo(() => {
    return ledgers.filter(
      (l) => l.group === "Bank Accounts" || l.group === "Cash-in-Hand"
    );
  }, [ledgers]);

  // Auto-select primary account when method changes
  useEffect(() => {
    if (paymentMethod === "Cash") {
      const cashLdg = bankAndCashLedgers.find((l) => l.group === "Cash-in-Hand");
      if (cashLdg) setSelectedAccountId(cashLdg.id);
    } else {
      const bankLdg = bankAndCashLedgers.find((l) => l.group === "Bank Accounts");
      if (bankLdg) setSelectedAccountId(bankLdg.id);
    }
  }, [paymentMethod, bankAndCashLedgers]);

  // Auto-select split account
  useEffect(() => {
    if (hasSplitPayment) {
      if (splitMethod === "Cash") {
        const cashLdg = bankAndCashLedgers.find((l) => l.group === "Cash-in-Hand");
        if (cashLdg) setSplitAccountId(cashLdg.id);
      } else {
        const bankLdg = bankAndCashLedgers.find((l) => l.group === "Bank Accounts" && l.id !== selectedAccountId);
        if (bankLdg) setSplitAccountId(bankLdg.id);
      }
    }
  }, [hasSplitPayment, splitMethod, bankAndCashLedgers, selectedAccountId]);

  // Set element ref in registry
  const setFieldRef = useCallback((itemId: string, field: string, el: HTMLElement | null) => {
    if (!fieldRefs.current[itemId]) {
      fieldRefs.current[itemId] = {};
    }
    fieldRefs.current[itemId][field] = el;
  }, []);

  // Reliable field focusing with direct refs or DOM selector fallback
  const focusRowField = useCallback((itemId: string, field: string) => {
    requestAnimationFrame(() => {
      const el = fieldRefs.current[itemId]?.[field];
      if (el) {
        el.focus();
        if ("select" in el && typeof (el as HTMLInputElement).select === "function") {
          (el as HTMLInputElement).select();
        }
        return;
      }

      const selector =
        field === "gst"
          ? `[data-item-id="${itemId}"] [data-field="gst"] button`
          : field === "description"
          ? `[data-item-id="${itemId}"] [data-field="description"] input`
          : `[data-item-id="${itemId}"] [data-field="${field}"]`;
      const found = formRef.current?.querySelector<HTMLElement>(selector);
      if (found) {
        found.focus();
        if ("select" in found && typeof (found as HTMLInputElement).select === "function") {
          (found as HTMLInputElement).select();
        }
      }
    });
  }, []);

  const focusField = useCallback((selector: string) => {
    requestAnimationFrame(() => {
      const el = formRef.current?.querySelector<HTMLElement>(selector);
      el?.focus();
      if (el && "select" in el && typeof (el as HTMLInputElement).select === "function") {
        (el as HTMLInputElement).select();
      }
    });
  }, []);

  // Helper to ensure an item exists in catalogue, creating it automatically if new
  const ensureCatalogueItem = useCallback(
    (line: InvoiceLine): StockItem | null => {
      const name = line.description?.trim();
      if (!name) return null;

      // 1. Check if item already exists by ID
      if (line.itemId) {
        const found = stockItems.find((s) => s.id === line.itemId);
        if (found) {
          if (line.isAutoCreated) {
            const isService = line.itemType === "service";
            const isPurchase = type === "Purchase";
            updateStockItem(found.id, {
              name,
              itemType: line.itemType || "goods",
              hsnCode: isService ? "" : (line.hsnCode || ""),
              sacCode: isService ? (line.sacCode || line.hsnCode || "") : "",
              unit: line.unit || (isService ? "Services" : "Nos"),
              costRate: Number(line.rate) || 0,
              // Strictly decouple selling rate from purchase rate
              standardRate: !isPurchase ? (Number(line.rate) || undefined) : found.standardRate,
              lastPurchaseRate: isPurchase ? (Number(line.rate) || null) : found.lastPurchaseRate,
              lastSellingRate: !isPurchase ? (Number(line.rate) || null) : found.lastSellingRate,
              gstRate: line.gstRate !== undefined ? Number(line.gstRate) : 18,
            });
          }
          return found;
        }
      }

      // 2. Check if item exists by exact name in current catalogue (case-insensitive)
      const existingByName = stockItems.find(
        (s) => s.name.trim().toLowerCase() === name.toLowerCase()
      );
      if (existingByName) {
        return existingByName;
      }

      // 3. Genuinely new catalogue item -> automatically create in inventory_items
      const isService = line.itemType === "service";
      const isPurchase = type === "Purchase";
      const newItem = addStockItem({
        name,
        itemType: line.itemType || "goods",
        stockGroup: isService ? "Services" : "General",
        unit: line.unit || (isService ? "Services" : "Nos"),
        hsnCode: isService ? "" : (line.hsnCode || ""),
        sacCode: isService ? (line.sacCode || line.hsnCode || "") : "",
        billingMethod: isService ? "one-time" : undefined,
        // Crucial: Purchases NEVER populate standardRate / selling rate
        standardRate: isPurchase ? undefined : (Number(line.rate) || undefined),
        costRate: Number(line.rate) || 0,
        currentRate: Number(line.rate) || 0,
        lastPurchaseRate: isPurchase ? (Number(line.rate) || null) : null,
        lastSellingRate: !isPurchase ? (Number(line.rate) || null) : null,
        sellingRateVerified: !isPurchase,
        gstRate: line.gstRate !== undefined ? Number(line.gstRate) : 18,
        openingQty: 0,
        openingRate: Number(line.rate) || 0,
        openingValue: 0,
        currentQty: 0,
        currentValue: 0,
        status: "active",
      });

      return newItem;
    },
    [stockItems, addStockItem, updateStockItem, type]
  );

  // Refresh authoritative stock from database whenever modal opens
  useEffect(() => {
    if (open) {
      refreshStockItems(activeCompany?.id);
    }
  }, [open, activeCompany?.id, refreshStockItems]);

  // Real-time calculation of quantities allocated in other rows of this unsaved invoice
  const getAllocatedQuantitiesExcludingRow = useCallback(
    (excludeRowId: string) => {
      const map: Record<string, number> = {};
      for (const r of items) {
        if (r.id === excludeRowId) continue;
        if (r.itemType === "service") continue;
        const q = typeof r.qty === "number" ? r.qty : parseFloat(String(r.qty)) || 0;
        if (q <= 0) continue;
        if (r.itemId) {
          map[r.itemId] = (map[r.itemId] || 0) + q;
        }
        if (r.description) {
          const norm = r.description.trim().toLowerCase();
          map[norm] = (map[norm] || 0) + q;
        }
      }
      return map;
    },
    [items]
  );

  // Maximum allowed stock for a specific row in the current invoice
  const getItemMaxStock = useCallback(
    (row: InvoiceLine) => {
      if (type !== "Sales" || row.itemType === "service") return Infinity;
      const matched = stockItems.find(
        (si) => si.id === row.itemId || si.name.trim().toLowerCase() === row.description.trim().toLowerCase()
      );
      if (!matched || matched.itemType === "service") return Infinity;

      let allocatedOther = 0;
      for (const other of items) {
        if (other.id === row.id) continue;
        if (other.itemType === "service") continue;
        if (
          (other.itemId && other.itemId === matched.id) ||
          (other.description && other.description.trim().toLowerCase() === matched.name.trim().toLowerCase())
        ) {
          allocatedOther += typeof other.qty === "number" ? other.qty : parseFloat(String(other.qty)) || 0;
        }
      }
      const persisted = Number(matched.currentQty || 0);
      return Math.max(0, Math.round((persisted - allocatedOther) * 100) / 100);
    },
    [type, stockItems, items]
  );

  // Atomic update of one or more fields in an item row
  const updateItemFields = useCallback((id: string, fields: Partial<InvoiceLine>) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const updated = { ...item, ...fields };
        const q = typeof updated.qty === "number" ? updated.qty : parseFloat(String(updated.qty)) || 0;
        const r = typeof updated.rate === "number" ? updated.rate : parseFloat(String(updated.rate)) || 0;
        const d = typeof updated.discount === "number" ? updated.discount : parseFloat(String(updated.discount)) || 0;
        const gross = Math.round(q * r * 100) / 100;
        updated.amount = Math.max(0, Math.round((gross - d) * 100) / 100);

        // If this item was auto-created in this session, safely sync changes to catalogue
        if (updated.itemId && updated.isAutoCreated) {
          const isService = updated.itemType === "service";
          const isPurchase = type === "Purchase";
          updateStockItem(updated.itemId, {
            name: updated.description.trim() || undefined,
            itemType: updated.itemType,
            hsnCode: isService ? "" : (updated.hsnCode || ""),
            sacCode: isService ? (updated.sacCode || updated.hsnCode || "") : "",
            unit: updated.unit || (isService ? "Services" : "Nos"),
            costRate: r,
            standardRate: !isPurchase ? r : undefined,
            lastPurchaseRate: isPurchase ? r : undefined,
            lastSellingRate: !isPurchase ? r : undefined,
            gstRate: updated.gstRate,
          });
        }
        return updated;
      })
    );
  }, [updateStockItem, type]);

  // Add new blank row and focus its Description input
  const addItemAndFocus = useCallback(() => {
    const newId = crypto.randomUUID();
    setItems((prev) => [
      ...prev,
      {
        id: newId,
        itemType: "goods",
        description: "",
        hsnCode: "",
        qty: 1,
        rate: 0,
        discount: 0,
        gstRate: 18,
        amount: 0,
        unit: "Nos",
      },
    ]);
    requestAnimationFrame(() => {
      setTimeout(() => {
        focusRowField(newId, "description");
      }, 50);
    });
  }, [focusRowField]);

  // Complete current row and create a new row or advance to next row
  const handleRowComplete = useCallback(
    (itemId: string) => {
      setItems((currentItems) => {
        const currentIndex = currentItems.findIndex((i) => i.id === itemId);
        if (currentIndex >= 0 && currentIndex < currentItems.length - 1) {
          const nextId = currentItems[currentIndex + 1].id;
          requestAnimationFrame(() => focusRowField(nextId, "description"));
          return currentItems;
        } else {
          const newId = crypto.randomUUID();
          requestAnimationFrame(() => {
            setTimeout(() => focusRowField(newId, "description"), 50);
          });
          return [
            ...currentItems,
            {
              id: newId,
              itemType: "goods",
              description: "",
              hsnCode: "",
              qty: 1,
              rate: 0,
              discount: 0,
              gstRate: 18,
              amount: 0,
              unit: "Nos",
            },
          ];
        }
      });
    },
    [focusRowField]
  );

  // Empty row Enter handling: move focus to Narration without creating endless blank rows
  const handleEmptyRowEnter = useCallback((itemId: string) => {
    setItems((prev) => {
      const currentRow = prev.find((i) => i.id === itemId);
      const validRows = prev.filter((i) => i.id !== itemId && i.description.trim().length > 0);

      if (validRows.length > 0 && (!currentRow || !currentRow.description.trim())) {
        return prev.filter((i) => i.id !== itemId);
      }
      return prev;
    });

    requestAnimationFrame(() => {
      narrationRef.current?.focus();
    });
  }, []);

  // Enter navigation inside a row
  const handleFieldEnter = useCallback(
    (e: React.KeyboardEvent, itemId: string, currentField: string) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      e.stopPropagation();

      if (currentField === "hsn") {
        focusRowField(itemId, "qty");
      } else if (currentField === "qty") {
        focusRowField(itemId, "rate");
      } else if (currentField === "rate") {
        focusRowField(itemId, "discount");
      } else if (currentField === "discount") {
        focusRowField(itemId, "gst");
      } else if (currentField === "gst") {
        handleRowComplete(itemId);
      }
    },
    [focusRowField, handleRowComplete]
  );

  // Focus party input when dialog opens
  useEffect(() => {
    if (open && fastEntry) {
      const t = setTimeout(() => focusField('[data-field="party-input"]'), 150);
      return () => clearTimeout(t);
    }
  }, [open, fastEntry, focusField]);

  // Global Ctrl+S and Alt+A handlers
  const handleSubmitRef = useRef<(action: "approve" | "draft" | "preview") => void>(null);

  const handleFormKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        e.stopPropagation();
        handleSubmitRef.current?.("approve");
        return;
      }
      if (e.altKey && e.key.toLowerCase() === "a") {
        e.preventDefault();
        e.stopPropagation();
        addItemAndFocus();
        return;
      }
    },
    [addItemAndFocus]
  );

  const removeItem = (id: string) => {
    if (items.length <= 1) return;
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const isInterState = useMemo(() => {
    if (!placeOfSupply) return false;
    return placeOfSupply.trim().toLowerCase() !== companyState.trim().toLowerCase();
  }, [placeOfSupply, companyState]);

  // Date safeguards & Financial Year validation
  const periodCheck = useMemo(() => {
    return validatePeriodLock(invoiceDate, activeCompany?.periodLockDate, role);
  }, [invoiceDate, activeCompany?.periodLockDate, role]);

  const fyInfo = useMemo(() => {
    const d = new Date(invoiceDate);
    if (isNaN(d.getTime())) return null;
    const m = d.getMonth() + 1; // 1-12
    const y = d.getFullYear();
    const fyStart = m >= 4 ? y : y - 1;
    const fyEnd = (fyStart + 1) % 100;
    const curYear = new Date().getFullYear();
    const curMonth = new Date().getMonth() + 1;
    const curFyStart = curMonth >= 4 ? curYear : curYear - 1;
    const isPastFY = fyStart < curFyStart;
    return {
      label: `FY ${fyStart}-${fyEnd < 10 ? "0" + fyEnd : fyEnd}`,
      isPastFY,
      isBackdated: invoiceDate !== new Date().toISOString().split("T")[0],
    };
  }, [invoiceDate]);

  // Duplicate Invoice Number Check
  const duplicateInvoiceWarning = useMemo(() => {
    if (!invoiceNumber.trim()) return null;
    const exists = vouchers.some(
      (v) =>
        v.invoiceNumber?.trim().toLowerCase() === invoiceNumber.trim().toLowerCase() &&
        v.type === type &&
        v.status !== "Cancelled"
    );
    return exists ? `Invoice number "${invoiceNumber}" is already in use by another voucher.` : null;
  }, [invoiceNumber, vouchers, type]);

  // Standard Ledger Resolution
  const stockAssetLedger = useMemo(() => {
    return (
      ledgers.find((l) => l.group === "Stock-in-Hand") ||
      ledgers.find((l) => l.name.toLowerCase().includes("stock-in-hand")) ||
      ledgers.find((l) => l.id.includes("stock"))
    );
  }, [ledgers]);

  const cogsLedger = useMemo(() => {
    return (
      ledgers.find(
        (l) =>
          l.group === "Direct Expenses" &&
          (l.name.toLowerCase().includes("cost of goods sold") || l.name.toLowerCase().includes("cogs"))
      ) || ledgers.find((l) => l.id.includes("cogs"))
    );
  }, [ledgers]);

  const purchaseAccountLedger = useMemo(() => {
    return (
      ledgers.find(
        (l) =>
          l.group === "Purchase Accounts" &&
          (l.name === "Purchase Account" || l.name.toLowerCase().includes("purchase account"))
      ) ||
      ledgers.find((l) => l.group === "Purchase Accounts") ||
      ledgers.find((l) => l.nature === "Expense")
    );
  }, [ledgers]);

  const salesAccountLedger = useMemo(() => {
    return (
      ledgers.find(
        (l) =>
          l.group === "Sales Accounts" &&
          (l.name === "Sales Account" || l.name.toLowerCase().includes("sales account"))
      ) ||
      ledgers.find((l) => l.group === "Sales Accounts") ||
      ledgers.find((l) => l.nature === "Income")
    );
  }, [ledgers]);

  // Recalculate totals immediately upon any row change or Place of Supply shift
  const totals = useMemo(() => {
    let grossTotal = 0;
    let totalDiscount = 0;
    let taxableTotal = 0;
    let cgstTotal = 0;
    let sgstTotal = 0;
    let igstTotal = 0;
    let cessTotal = 0;

    const lineDetails = items.map((item) => {
      const q = typeof item.qty === "number" ? item.qty : parseFloat(String(item.qty)) || 0;
      const r = typeof item.rate === "number" ? item.rate : parseFloat(String(item.rate)) || 0;
      const d = typeof item.discount === "number" ? item.discount : parseFloat(String(item.discount)) || 0;
      const gross = Math.round(q * r * 100) / 100;
      const lineTaxable = Math.max(0, Math.round((gross - d) * 100) / 100);
      const gst = calculateGST(lineTaxable, Number(item.gstRate) || 0, isInterState);

      grossTotal += gross;
      totalDiscount += d;
      taxableTotal += gst.taxableAmount;
      cgstTotal += gst.cgstAmount;
      sgstTotal += gst.sgstAmount;
      igstTotal += gst.igstAmount;
      cessTotal += gst.cessAmount;
      return { ...item, gross, discount: d, amount: lineTaxable, gst };
    });

    const totalTax = cgstTotal + sgstTotal + igstTotal + cessTotal;
    const grandTotal = Math.round((taxableTotal + totalTax) * 100) / 100;

    return { grossTotal, totalDiscount, taxableTotal, cgstTotal, sgstTotal, igstTotal, cessTotal, totalTax, grandTotal, lineDetails };
  }, [items, isInterState]);

  // Update paid amount when full payment is active or totals shift
  useEffect(() => {
    if (settlementMode === "full") {
      setPaidAmount(totals.grandTotal);
    } else if (settlementMode === "credit") {
      setPaidAmount(0);
    }
  }, [settlementMode, totals.grandTotal]);

  // Live outstanding receivable / payable
  const effectivePaid = settlementMode === "credit" ? 0 : Math.min(totals.grandTotal, Math.max(0, paidAmount));
  const outstandingAmount = Math.max(0, Math.round((totals.grandTotal - effectivePaid) * 100) / 100);

  // Unified Transaction Submission
  const handleSubmit = async (action: "approve" | "draft" | "preview" = "approve") => {
    if (submitting) return;

    if (!periodCheck.allowed) {
      toast({
        title: "Period Locked",
        description: periodCheck.error || "Transaction date falls in a locked accounting period.",
        variant: "destructive",
      });
      return;
    }

    if (!partyLedgerId) {
      toast({
        title: "Validation Error",
        description: `Please select a ${isCustomerType ? "customer" : "supplier"}.`,
        variant: "destructive",
      });
      focusField('[data-field="party-input"]');
      return;
    }

    if (!placeOfSupply) {
      toast({
        title: "Validation Error",
        description: "Please specify the Place of Supply state.",
        variant: "destructive",
      });
      const trigger = formRef.current?.querySelector<HTMLButtonElement>('[data-field="place-of-supply"] button');
      trigger?.click();
      return;
    }

    const validItems = items.filter((i) => i.description.trim() !== "");
    if (validItems.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please add at least one item or service description.",
        variant: "destructive",
      });
      focusField('[data-item-id] [data-field="description"] input');
      return;
    }

    // Validation for item lines and automatic catalogue item association
    for (const itm of validItems) {
      if (itm.qty <= 0) {
        toast({
          title: "Validation Error",
          description: `Quantity for "${itm.description}" must be greater than zero.`,
          variant: "destructive",
        });
        return;
      }
      if (itm.rate < 0) {
        toast({
          title: "Validation Error",
          description: `Rate for "${itm.description}" cannot be negative.`,
          variant: "destructive",
        });
        return;
      }
      if (itm.discount < 0) {
        toast({
          title: "Validation Error",
          description: `Discount for "${itm.description}" cannot be negative.`,
          variant: "destructive",
        });
        return;
      }

      // Automatically ensure catalogue record exists and assign itemId
      if (!itm.itemId) {
        const ensured = ensureCatalogueItem(itm);
        if (ensured) {
          itm.itemId = ensured.id;
        } else {
          toast({
            title: "Catalogue Error",
            description: `Could not create or associate catalogue record for "${itm.description}".`,
            variant: "destructive",
          });
          return;
        }
      }
    }

    if (totals.taxableTotal <= 0) {
      toast({
        title: "Validation Error",
        description: "Invoice amount must be greater than zero.",
        variant: "destructive",
      });
      return;
    }

    const partyLedger =
      ledgers.find((l) => l.id === partyLedgerId) ||
      parties.find((p) => p.debtorLedgerId === partyLedgerId || p.creditorLedgerId === partyLedgerId) ||
      clients.find((c) => c.id === partyLedgerId);

    if (!partyLedger) {
      toast({ title: "Validation Error", description: "Selected party account was not found.", variant: "destructive" });
      return;
    }

    // Resolve GST Ledgers
    const isSales = type === "Sales" || type === "Credit Note";
    const cgstLedger = ledgers.find((l) =>
      isSales
        ? l.name.toLowerCase().includes("cgst out") || l.id.includes("cgst-out") || (l.group === "Duties & Taxes" && l.name.toLowerCase().includes("cgst") && !l.name.toLowerCase().includes("in"))
        : l.name.toLowerCase().includes("cgst in") || l.id.includes("cgst-in") || (l.group === "Duties & Taxes" && l.name.toLowerCase().includes("cgst") && !l.name.toLowerCase().includes("out"))
    );
    const sgstLedger = ledgers.find((l) =>
      isSales
        ? l.name.toLowerCase().includes("sgst out") || l.id.includes("sgst-out") || (l.group === "Duties & Taxes" && l.name.toLowerCase().includes("sgst") && !l.name.toLowerCase().includes("in"))
        : l.name.toLowerCase().includes("sgst in") || l.id.includes("sgst-in") || (l.group === "Duties & Taxes" && l.name.toLowerCase().includes("sgst") && !l.name.toLowerCase().includes("out"))
    );
    const igstLedger = ledgers.find((l) =>
      isSales
        ? l.name.toLowerCase().includes("igst out") || l.id.includes("igst-out") || (l.group === "Duties & Taxes" && l.name.toLowerCase().includes("igst") && !l.name.toLowerCase().includes("in"))
        : l.name.toLowerCase().includes("igst in") || l.id.includes("igst-in") || (l.group === "Duties & Taxes" && l.name.toLowerCase().includes("igst") && !l.name.toLowerCase().includes("out"))
    );

    if (isInterState && totals.igstTotal > 0 && !igstLedger) {
      const report = diagnoseInvoicePrerequisites({
        company: activeCompany,
        ledgers,
        type,
        hasGstItems: validItems.some((i) => i.gstRate > 0),
        isInterState,
        cgstAmount: totals.cgstTotal,
        sgstAmount: totals.sgstTotal,
        igstAmount: totals.igstTotal,
      });
      setDiagnosticReport(report);
      setInlineRepairError("Missing Ledger: IGST Output/Input ledger not found in Duties & Taxes");
      setShowRepairDialog(true);
      toast({
        title: "Missing Ledger",
        description: "IGST Output/Input ledger not found. Click 'Repair Now' to safely create it.",
        variant: "destructive",
      });
      return;
    }

    if (!isInterState && (totals.cgstTotal > 0 || totals.sgstTotal > 0) && (!cgstLedger || !sgstLedger)) {
      const report = diagnoseInvoicePrerequisites({
        company: activeCompany,
        ledgers,
        type,
        hasGstItems: validItems.some((i) => i.gstRate > 0),
        isInterState,
        cgstAmount: totals.cgstTotal,
        sgstAmount: totals.sgstTotal,
        igstAmount: totals.igstTotal,
      });
      setDiagnosticReport(report);
      setInlineRepairError("Missing Ledger: CGST/SGST Output/Input ledger not found in Duties & Taxes");
      setShowRepairDialog(true);
      toast({
        title: "Missing Ledger",
        description: "CGST/SGST Output/Input ledger not found. Click 'Repair Now' to safely create it.",
        variant: "destructive",
      });
      return;
    }

    // Authoritative real-time physical stock availability validation for Sales invoices
    if (type === "Sales" && action !== "draft") {
      const itemTotals: Record<string, { qty: number; name: string; maxStock: number }> = {};
      for (const row of validItems) {
        if (row.itemType === "service") continue;
        const matched = (row.itemId ? stockItems.find((si) => si.id === row.itemId) : null) ||
          stockItems.find(
            (si) => si.name.trim().toLowerCase() === row.description.trim().toLowerCase()
          );
        if (matched && matched.itemType !== "service") {
          const key = matched.id;
          if (!itemTotals[key]) {
            itemTotals[key] = { qty: 0, name: matched.name, maxStock: Number(matched.currentQty || 0) };
          }
          itemTotals[key].qty += Number(row.qty) || 0;
        } else if (!matched && row.itemType !== "service" && row.description.trim()) {
          toast({
            title: "Item Not In Inventory",
            description: `"${row.description}" does not exist in inventory with available stock. Physical goods cannot be sold without available inventory.`,
            variant: "destructive",
          });
          return;
        }
      }

      for (const [key, info] of Object.entries(itemTotals)) {
        const roundedQty = Math.round(info.qty * 100) / 100;
        const roundedMax = Math.round(info.maxStock * 100) / 100;
        if (roundedQty > roundedMax) {
          toast({
            title: "Stock Limit Exceeded",
            description: `Cannot save sale invoice: Total requested quantity for "${info.name}" (${roundedQty}) exceeds available inventory stock (${roundedMax}). Please adjust quantities.`,
            variant: "destructive",
          });
          return;
        }
      }
    }

    setSubmitting(true);
    try {
      const isDraft = action === "draft";
      const lines: VoucherLine[] = [];

      // Categorize lines into Physical Goods vs Non-Stock Services
      const goodsLines = validItems.filter((i) => i.itemType !== "service");
      const serviceLines = validItems.filter((i) => i.itemType === "service");

      const goodsTaxable = Math.round(goodsLines.reduce((s, i) => s + i.amount, 0) * 100) / 100;
      const serviceTaxable = Math.round(serviceLines.reduce((s, i) => s + i.amount, 0) * 100) / 100;

      if (type === "Purchase") {
        // Physical goods purchases MUST debit Stock-in-Hand / Inventory Asset
        if (goodsTaxable > 0) {
          const targetStockLedger = stockAssetLedger || purchaseAccountLedger;
          lines.push({
            id: crypto.randomUUID(),
            ledgerId: targetStockLedger!.id,
            ledgerName: targetStockLedger!.name,
            debit: goodsTaxable,
            credit: 0,
          });
        }
        // Non-stock service purchases debit Purchase Account / Direct Expense
        if (serviceTaxable > 0) {
          const targetPurchaseLedger = purchaseAccountLedger || stockAssetLedger;
          lines.push({
            id: crypto.randomUUID(),
            ledgerId: targetPurchaseLedger!.id,
            ledgerName: targetPurchaseLedger!.name,
            debit: serviceTaxable,
            credit: 0,
          });
        }
        // Supplier is credited for the grand total invoice amount
        lines.push({
          id: crypto.randomUUID(),
          ledgerId: partyLedgerId,
          ledgerName: partyLedger.name,
          debit: 0,
          credit: Math.round(totals.grandTotal * 100) / 100,
        });
      } else if (type === "Sales") {
        // Customer is debited for the grand total invoice amount
        lines.push({
          id: crypto.randomUUID(),
          ledgerId: partyLedgerId,
          ledgerName: partyLedger.name,
          debit: Math.round(totals.grandTotal * 100) / 100,
          credit: 0,
        });
        // Sales Account is credited for the taxable total
        const targetSalesLedger = salesAccountLedger!;
        lines.push({
          id: crypto.randomUUID(),
          ledgerId: targetSalesLedger.id,
          ledgerName: targetSalesLedger.name,
          debit: 0,
          credit: Math.round(totals.taxableTotal * 100) / 100,
        });
      } else if (type === "Credit Note") {
        lines.push({
          id: crypto.randomUUID(),
          ledgerId: salesAccountLedger?.id || stockAssetLedger!.id,
          ledgerName: salesAccountLedger?.name || stockAssetLedger!.name,
          debit: Math.round(totals.taxableTotal * 100) / 100,
          credit: 0,
        });
        lines.push({
          id: crypto.randomUUID(),
          ledgerId: partyLedgerId,
          ledgerName: partyLedger.name,
          debit: 0,
          credit: Math.round(totals.grandTotal * 100) / 100,
        });
      } else if (type === "Debit Note") {
        lines.push({
          id: crypto.randomUUID(),
          ledgerId: partyLedgerId,
          ledgerName: partyLedger.name,
          debit: Math.round(totals.grandTotal * 100) / 100,
          credit: 0,
        });
        lines.push({
          id: crypto.randomUUID(),
          ledgerId: purchaseAccountLedger?.id || stockAssetLedger!.id,
          ledgerName: purchaseAccountLedger?.name || stockAssetLedger!.name,
          debit: 0,
          credit: Math.round(totals.taxableTotal * 100) / 100,
        });
      }

      // GST entries
      if (isInterState && igstLedger && totals.igstTotal > 0) {
        if (type === "Sales" || type === "Debit Note") {
          lines.push({ id: crypto.randomUUID(), ledgerId: igstLedger.id, ledgerName: igstLedger.name, debit: 0, credit: Math.round(totals.igstTotal * 100) / 100 });
        } else {
          lines.push({ id: crypto.randomUUID(), ledgerId: igstLedger.id, ledgerName: igstLedger.name, debit: Math.round(totals.igstTotal * 100) / 100, credit: 0 });
        }
      } else {
        if (cgstLedger && totals.cgstTotal > 0) {
          if (type === "Sales" || type === "Debit Note") {
            lines.push({ id: crypto.randomUUID(), ledgerId: cgstLedger.id, ledgerName: cgstLedger.name, debit: 0, credit: Math.round(totals.cgstTotal * 100) / 100 });
          } else {
            lines.push({ id: crypto.randomUUID(), ledgerId: cgstLedger.id, ledgerName: cgstLedger.name, debit: Math.round(totals.cgstTotal * 100) / 100, credit: 0 });
          }
        }
        if (sgstLedger && totals.sgstTotal > 0) {
          if (type === "Sales" || type === "Debit Note") {
            lines.push({ id: crypto.randomUUID(), ledgerId: sgstLedger.id, ledgerName: sgstLedger.name, debit: 0, credit: Math.round(totals.sgstTotal * 100) / 100 });
          } else {
            lines.push({ id: crypto.randomUUID(), ledgerId: sgstLedger.id, ledgerName: sgstLedger.name, debit: Math.round(totals.sgstTotal * 100) / 100, credit: 0 });
          }
        }
      }

      const invoiceItems: InvoiceItem[] = validItems.map((i) => {
        let resolvedItemId = i.itemId;
        if (!resolvedItemId) {
          const matched = stockItems.find(
            (si) => si.name.trim().toLowerCase() === i.description.trim().toLowerCase()
          );
          if (matched) resolvedItemId = matched.id;
        }
        return {
          id: resolvedItemId || undefined,
          itemId: resolvedItemId || undefined,
          name: i.description.trim(),
          description: i.description.trim(),
          itemType: i.itemType || "goods",
          hsnCode: i.hsnCode || "",
          sacCode: i.sacCode || "",
          qty: Number(i.qty) || 1,
          rate: Number(i.rate) || 0,
          discount: Number(i.discount) || 0,
          gstRate: Number(i.gstRate) || 0,
          amount: Math.round(i.amount * 100) / 100,
          unit: i.unit || (i.itemType === "service" ? "Services" : "Nos"),
        };
      });

      const finalNarration = narration.trim() || `GST ${type} Invoice - ${partyLedger.name}`;
      const actualPaidAmount = settlementMode === "credit" ? 0 : effectivePaid;
      const paymentStatus =
        actualPaidAmount >= totals.grandTotal - 0.01 ? "Paid" : actualPaidAmount > 0 ? "Partially Paid" : "Unpaid";

      const selectedAccount = bankAndCashLedgers.find((l) => l.id === selectedAccountId);

      // Create primary voucher atomically
      const result = await createVoucher(
        type,
        lines,
        finalNarration,
        partyLedgerId,
        invoiceNumber.trim() || undefined,
        Math.round(totals.totalTax * 100) / 100,
        Math.round(totals.cgstTotal * 100) / 100,
        Math.round(totals.sgstTotal * 100) / 100,
        Math.round(totals.igstTotal * 100) / 100,
        invoiceItems,
        {
          date: invoiceDate,
          status: isDraft ? "Draft" : undefined,
          paymentMode: settlementMode === "credit" ? "Credit" : paymentMethod,
          instrumentNo: referenceNo.trim() || undefined,
          instrumentDate: paymentDate,
          bankName: selectedAccount?.name,
          bankLedgerId: selectedAccount?.id,
          paidAmount: actualPaidAmount,
          paymentStatus: paymentStatus,
        }
      );

      if (!result.success || !result.voucher) {
        toast({ title: "Creation Error", description: result.error || "Failed to create invoice.", variant: "destructive" });
        await refreshStockItems(activeCompany?.id);
        setSubmitting(false);
        return;
      }

      const createdVoucher = result.voucher;

      // Atomic Immediate Payment Settlement Posting
      if (!isDraft && actualPaidAmount > 0 && selectedAccount) {
        const isSale = type === "Sales";
        const paymentVoucherType: VoucherType = isSale ? "Receipt" : "Payment";
        const paymentLines: VoucherLine[] = isSale
          ? [
              // Receipt: Debit Bank/Cash Account, Credit Customer
              {
                id: crypto.randomUUID(),
                ledgerId: selectedAccount.id,
                ledgerName: selectedAccount.name,
                debit: actualPaidAmount,
                credit: 0,
              },
              {
                id: crypto.randomUUID(),
                ledgerId: partyLedgerId,
                ledgerName: partyLedger.name,
                debit: 0,
                credit: actualPaidAmount,
              },
            ]
          : [
              // Payment: Debit Supplier, Credit Bank/Cash Account
              {
                id: crypto.randomUUID(),
                ledgerId: partyLedgerId,
                ledgerName: partyLedger.name,
                debit: actualPaidAmount,
                credit: 0,
              },
              {
                id: crypto.randomUUID(),
                ledgerId: selectedAccount.id,
                ledgerName: selectedAccount.name,
                debit: 0,
                credit: actualPaidAmount,
              },
            ];

        await createVoucher(
          paymentVoucherType,
          paymentLines,
          `${paymentVoucherType} settled for ${createdVoucher.voucherNumber} via ${paymentMethod}${referenceNo ? ` (Ref: ${referenceNo})` : ""}`,
          partyLedgerId,
          createdVoucher.voucherNumber,
          0, 0, 0, 0,
          undefined,
          {
            date: paymentDate,
            paymentMode: paymentMethod,
            instrumentNo: referenceNo.trim() || undefined,
            bankName: selectedAccount.name,
            bankLedgerId: selectedAccount.id,
            allocations: [
              {
                invoiceId: createdVoucher.id,
                invoiceNumber: createdVoucher.voucherNumber,
                allocatedAmount: actualPaidAmount,
              },
            ],
          }
        );
      }

      // Cost of Goods Sold (COGS) Recognition for Physical Goods in Approved Sales
      if (!isDraft && type === "Sales" && goodsLines.length > 0 && cogsLedger && stockAssetLedger) {
        let totalCOGS = 0;
        for (const gl of goodsLines) {
          const item = (gl.itemId ? stockItems.find((si) => si.id === gl.itemId) : null) ||
            stockItems.find(
              (si) => si.name.toLowerCase() === gl.description.toLowerCase()
            );
          // Use true Weighted Average Cost (currentRate / costRate), never selling price
          const unitCost = Number(item?.currentRate || item?.costRate || 0);
          totalCOGS += gl.qty * unitCost;
        }

        if (totalCOGS > 0) {
          const cogsRounded = Math.round(totalCOGS * 100) / 100;
          await createVoucher(
            "Journal",
            [
              {
                id: crypto.randomUUID(),
                ledgerId: cogsLedger.id,
                ledgerName: cogsLedger.name,
                debit: cogsRounded,
                credit: 0,
              },
              {
                id: crypto.randomUUID(),
                ledgerId: stockAssetLedger.id,
                ledgerName: stockAssetLedger.name,
                debit: 0,
                credit: cogsRounded,
              },
            ],
            `COGS recognition for Sale ${createdVoucher.voucherNumber} (${goodsLines.map((g) => g.description).join(", ")})`,
            undefined,
            createdVoucher.voucherNumber,
            0, 0, 0, 0,
            undefined,
            { date: invoiceDate }
          );
        }
      }

      toast({
        title: isDraft ? `Draft Saved` : `GST ${type} Invoice Created`,
        description: `${createdVoucher.voucherNumber} — ₹${totals.grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })} ${
          settlementMode !== "credit" ? `(${paymentStatus})` : "(Credit)"
        }`,
      });

      if (action === "preview") {
        setSavedVoucherForPreview(createdVoucher);
        setPreviewOpen(true);
        resetForm();
        onSuccess?.();
      } else {
        setOpen(false);
        resetForm();
        onSuccess?.();
      }
    } catch (err: any) {
      console.error("[GST Invoice Submit Error]:", err);
      toast({ title: "Error", description: err.message || "Failed to submit invoice.", variant: "destructive" });
      await refreshStockItems(activeCompany?.id);
    } finally {
      setSubmitting(false);
    }
  };

  handleSubmitRef.current = handleSubmit;

  const resetForm = () => {
    setPartyLedgerId("");
    setPlaceOfSupply("");
    setInvoiceNumber("");
    setNarration("");
    setPartyGstin("");
    setSettlementMode("credit");
    setPaymentMethod("Bank Account");
    setPaidAmount(0);
    setReferenceNo("");
    setHasSplitPayment(false);
    setItems([
      {
        id: crypto.randomUUID(),
        itemType: "goods",
        description: "",
        hsnCode: "",
        qty: 1,
        rate: 0,
        discount: 0,
        gstRate: 18,
        amount: 0,
        unit: "Nos",
      },
    ]);
  };

  if (!perms.canCreate) return null;

  return (
    <>
      <FormModal
        open={open}
        onOpenChange={setOpen}
        size="transaction"
        trigger={
          trigger ? (
            trigger
          ) : isOpen === undefined ? (
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm font-semibold">
              <Plus className="h-4 w-4 mr-2" /> New {type === "Sales" ? "Sale" : type === "Purchase" ? "Purchase" : type}
            </Button>
          ) : undefined
        }
        icon={<FileText className="h-5 w-5" />}
        title={type === "Sales" ? "New GST Sale Invoice" : type === "Purchase" ? "New GST Purchase Bill" : `GST ${type}`}
        description="Unified Fast Transaction — Invoice, Inventory, GST, & Instant Settlement in One Screen"
        badge={
          fyInfo ? (
            <Badge variant="outline" className={`text-xs font-mono ${fyInfo.isPastFY ? "border-amber-500/50 bg-amber-500/10 text-amber-600" : ""}`}>
              <Calendar className="h-3 w-3 mr-1" />
              {fyInfo.label}
              {fyInfo.isPastFY && " · Missed Bill"}
            </Badge>
          ) : null
        }
        headerActions={
          <button
            type="button"
            onClick={() => setFastEntry((f) => !f)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
              fastEntry
                ? "bg-primary/15 text-primary border border-primary/30"
                : "bg-muted text-muted-foreground border border-border"
            }`}
          >
            <Zap className="h-3 w-3" />
            Fast Entry {fastEntry ? "ON" : "OFF"}
          </button>
        }
        footerLeft={
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={submitting}
              className="text-xs h-9"
            >
              Cancel <span className="text-[10px] text-muted-foreground ml-1">Esc</span>
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleSubmit("draft")}
              disabled={submitting || !periodCheck.allowed}
              className="text-xs h-9 gap-1.5 text-muted-foreground hover:text-foreground"
            >
              <FileText className="h-3.5 w-3.5" />
              <span>Save as Draft</span>
            </Button>
          </div>
        }
        footerRight={
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={() => handleSubmit("approve")}
              disabled={submitting || !periodCheck.allowed}
              className="bg-card hover:bg-muted border border-border text-foreground text-xs h-9 font-semibold gap-1.5 shadow-2xs"
            >
              {submitting ? (
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5 text-emerald-600" />
              )}
              <span>Save & Close</span>
            </Button>

            <Button
              type="button"
              onClick={() => handleSubmit("preview")}
              disabled={submitting || !periodCheck.allowed}
              className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs h-9 font-semibold gap-1.5 shadow-sm"
            >
              {submitting ? (
                <>
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  <span>Saving Transaction...</span>
                </>
              ) : (
                <>
                  <Printer className="h-3.5 w-3.5" />
                  <span>Save & Print / Preview</span>
                  {fastEntry && (
                    <kbd className="ml-1 text-[10px] px-1 py-0.5 rounded bg-primary-foreground/20 font-mono">
                      Ctrl+S
                    </kbd>
                  )}
                </>
              )}
            </Button>
          </div>
        }
        onKeyDown={handleFormKeyDown}
      >
        {/* Period Lock Warning */}
        {!periodCheck.allowed && (
          <div className="flex items-center gap-2.5 p-3 rounded-xl border border-destructive/40 bg-destructive/10 text-destructive text-xs font-medium">
            <Lock className="h-4 w-4 shrink-0" />
            <span>{periodCheck.error}</span>
          </div>
        )}

        {/* Duplicate Invoice Warning */}
        {duplicateInvoiceWarning && (
          <div className="flex items-center gap-2.5 p-3 rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-200 text-xs">
            <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" />
            <span>{duplicateInvoiceWarning}</span>
          </div>
        )}

        {/* Accounting Configuration Diagnostic Alert */}
        {inlineRepairError && (
          <div className="flex items-center justify-between p-3 rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-200">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" />
              <div>
                <span className="text-xs font-semibold block">Missing Accounting Configuration</span>
                <span className="text-xs text-muted-foreground">{inlineRepairError}</span>
              </div>
            </div>
            <Button
              type="button"
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-8 gap-1.5 shrink-0"
              onClick={() => {
                const report = diagnoseInvoicePrerequisites({
                  company: activeCompany,
                  ledgers,
                  type,
                  hasGstItems: items.some((i) => i.description.trim() && i.qty > 0 && i.rate > 0 && i.gstRate > 0),
                  isInterState,
                  cgstAmount: totals.cgstTotal,
                  sgstAmount: totals.sgstTotal,
                  igstAmount: totals.igstTotal,
                });
                setDiagnosticReport(report);
                setShowRepairDialog(true);
              }}
            >
              <Wrench className="h-3.5 w-3.5" />
              Repair Now
            </Button>
          </div>
        )}

          <div ref={formRef} className="space-y-4">
            {/* Section 2: Transaction Details */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-2xs space-y-3.5">
              <div className="flex items-center gap-2 border-b border-border/50 pb-2.5">
                <Building2 className="h-4 w-4 text-primary" />
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Transaction & Party Details
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
                {/* Party Autocomplete with Inline Quick Creation */}
                <div data-field="party" className="sm:col-span-2 lg:col-span-1">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1.5">
                    {isCustomerType ? "Customer" : "Supplier"} *
                    {fastEntry && <span className="ml-1 text-[10px] text-accent font-normal">Enter → next</span>}
                  </label>
                  <PartyAutocomplete
                    value={partyLedgerId}
                    onSelect={(id, ledger, party) => {
                      setPartyLedgerId(id);
                      const effectiveState = party?.billingState || party?.shippingState || ledger?.state;
                      if (effectiveState) {
                        setPlaceOfSupply(effectiveState);
                      }
                      if (party?.gstin) {
                        setPartyGstin(party.gstin);
                      } else if (ledger?.gstin) {
                        setPartyGstin(ledger.gstin);
                      } else {
                        setPartyGstin("");
                      }

                      if (fastEntry) {
                        if (effectiveState) {
                          setTimeout(() => {
                            if (items.length > 0) focusRowField(items[0].id, "description");
                          }, 100);
                        } else {
                          setTimeout(() => {
                            const triggerEl = formRef.current?.querySelector<HTMLButtonElement>('[data-field="place-of-supply"] button');
                            triggerEl?.click();
                          }, 100);
                        }
                      }
                    }}
                    partyType={isCustomerType ? "customer" : "supplier"}
                  />
                </div>

                {/* Invoice Number */}
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1.5">
                    Invoice / Bill No.
                  </label>
                  <input
                    type="text"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    placeholder="Auto-generated if blank"
                    className="h-9 w-full px-3 text-xs rounded-md border border-border bg-card outline-none focus:ring-1 focus:ring-primary font-mono"
                  />
                </div>

                {/* Invoice Date */}
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1.5 flex items-center justify-between">
                    <span>Transaction Date *</span>
                    {fyInfo?.isBackdated && (
                      <span className="text-[10px] text-amber-500 font-semibold">Backdated</span>
                    )}
                  </label>
                  <input
                    type="date"
                    value={invoiceDate}
                    onChange={(e) => {
                      setInvoiceDate(e.target.value);
                      setPaymentDate(e.target.value);
                    }}
                    className="h-9 w-full px-3 text-xs rounded-md border border-border bg-card outline-none focus:ring-1 focus:ring-primary font-mono"
                  />
                </div>

                {/* Place of Supply */}
                <div data-field="place-of-supply" onKeyDown={(e) => {
                  if (e.key === "Enter" && items.length > 0) {
                    e.preventDefault();
                    focusRowField(items[0].id, "description");
                  }
                }}>
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1.5">
                    Place of Supply *
                  </label>
                  <Select
                    value={placeOfSupply}
                    onValueChange={(v) => {
                      setPlaceOfSupply(v);
                      if (fastEntry && items.length > 0) {
                        setTimeout(() => focusRowField(items[0].id, "description"), 100);
                      }
                    }}
                  >
                    <SelectTrigger className="h-9 text-xs">
                      <SelectValue placeholder="Select supply state..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-56">
                      {INDIAN_STATES.map((s) => (
                        <SelectItem key={s} value={s} className="text-xs">
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Section 3: GST & Supply Information */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3.5 sm:p-4 rounded-xl border border-slate-200 bg-white shadow-2xs text-xs">
              <div className="flex items-center gap-2.5 w-full sm:w-auto">
                <span className="text-muted-foreground font-semibold uppercase text-[11px] tracking-wider shrink-0">Party GSTIN:</span>
                <input
                  type="text"
                  value={partyGstin}
                  onChange={(e) => setPartyGstin(e.target.value.toUpperCase())}
                  placeholder="Unregistered / B2C"
                  className="h-8 px-2.5 text-xs font-mono font-medium rounded-md border border-border bg-background w-48 outline-none focus:ring-1 focus:ring-primary uppercase"
                />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge
                  variant="outline"
                  className={`text-xs font-medium px-2.5 py-1 ${
                    isInterState
                      ? "border-blue-500/40 bg-blue-500/10 text-blue-600 dark:text-blue-400"
                      : "border-indigo-500/40 bg-indigo-500/10 text-indigo-600 dark:text-indigo-400"
                  }`}
                >
                  {isInterState ? "⚡ Inter-State (IGST 100%)" : "🏠 Intra-State (CGST 50% + SGST 50%)"}
                </Badge>
                <span className="text-[11px] text-muted-foreground hidden sm:inline">
                  Origin: <strong className="text-foreground font-medium">{companyState}</strong> → Supply: <strong className="text-foreground font-medium">{placeOfSupply || "Not set"}</strong>
                </span>
              </div>
            </div>

            {/* Section 4: Line Items Table */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-2xs space-y-3">
              <div className="flex items-center justify-between border-b border-border/50 pb-2.5">
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-primary" />
                  <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Goods & Services Line Items
                  </span>
                </div>
                <span className="text-[11px] text-muted-foreground hidden sm:inline">
                  Goods auto-update inventory & COGS · Services post to revenue/expense
                </span>
              </div>

              {/* Desktop Headers */}
              <div className="hidden lg:grid grid-cols-[110px_1fr_95px_75px_95px_80px_75px_95px_36px] gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
                <span>Type</span>
                <span>Description *</span>
                <span>HSN / SAC</span>
                <span className="text-right">Qty</span>
                <span className="text-right">Rate (₹)</span>
                <span className="text-right">Disc (₹)</span>
                <span className="text-right">GST %</span>
                <span className="text-right">Taxable (₹)</span>
                <span></span>
              </div>

              {items.map((item, idx) => (
                <div key={item.id} data-item-id={item.id} className="relative" style={{ zIndex: (items.length - idx) * 10 }}>
                  {/* Desktop Row */}
                  <div className="hidden lg:grid grid-cols-[110px_1fr_95px_75px_95px_80px_75px_95px_36px] gap-2 items-center">
                    {/* Item Type Switcher / Badge */}
                    <div>
                      <Select
                        value={item.itemType || "goods"}
                        onValueChange={(val: "goods" | "service") => {
                          updateItemFields(item.id, {
                            itemType: val,
                            unit: val === "service" ? "Services" : "Nos",
                          });
                        }}
                      >
                        <SelectTrigger className="h-9 text-[11px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="goods" className="text-xs">
                            <span className="flex items-center gap-1 font-medium text-blue-600">
                              📦 Goods
                            </span>
                          </SelectItem>
                          <SelectItem value="service" className="text-xs">
                            <span className="flex items-center gap-1 font-medium text-purple-600">
                              ⚡ Service
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Description Autocomplete */}
                    <div data-field="description">
                      <ItemAutocomplete
                        value={item.description}
                        transactionType={type}
                        allocatedQuantities={getAllocatedQuantitiesExcludingRow(item.id)}
                        currentRowId={item.id}
                        onChange={(v) => updateItemFields(item.id, { description: v })}
                        onSelectItem={(sel: SelectedItemPayload) => {
                          const isService = sel.itemType === "service";
                          const effectiveRate = Number(sel.rate) || 0;
                          const code = isService ? sel.sacCode || sel.hsnCode || "" : sel.hsnCode || "";

                          // If Sales and physical goods, clamp initial quantity to remaining available stock
                          let initialQty = item.qty || 1;
                          if (type === "Sales" && !isService) {
                            const allocMap = getAllocatedQuantitiesExcludingRow(item.id);
                            const currentPersisted = sel.id
                              ? Number(stockItems.find((s) => s.id === sel.id)?.currentQty ?? sel.currentQty ?? 0)
                              : Number(sel.currentQty ?? 0);
                            const allocatedOther = (sel.id && allocMap[sel.id]) || allocMap[sel.name.trim().toLowerCase()] || 0;
                            const availableStock = Math.max(0, currentPersisted - allocatedOther);
                            if (availableStock > 0 && initialQty > availableStock) {
                              initialQty = availableStock;
                            }
                          }

                          // 1. Existing catalogue item selected
                          if (sel.id) {
                            updateItemFields(item.id, {
                              itemId: sel.id,
                              description: sel.name,
                              itemType: sel.itemType,
                              hsnCode: code,
                              sacCode: sel.sacCode || "",
                              qty: initialQty,
                              rate: effectiveRate > 0 ? effectiveRate : item.rate,
                              unit: sel.unit || (isService ? "Services" : "Nos"),
                              gstRate: sel.gstRate !== undefined ? Number(sel.gstRate) : item.gstRate,
                              isAutoCreated: false,
                            });
                          } else {
                            // 2. Check if item already exists in catalogue by name
                            const existing = stockItems.find(
                              (si) => si.name.trim().toLowerCase() === sel.name.trim().toLowerCase()
                            );
                            if (existing) {
                              const isPurchase = type === "Purchase";
                              const suggestedRate = isPurchase
                                ? Number(existing.lastPurchaseRate || existing.costRate || existing.currentRate || 0)
                                : Number(existing.lastSellingRate || (existing.sellingRateVerified ? existing.standardRate : null) || 0);

                              updateItemFields(item.id, {
                                itemId: existing.id,
                                description: existing.name,
                                itemType: existing.itemType,
                                hsnCode: existing.hsnCode || existing.sacCode || "",
                                sacCode: existing.sacCode || "",
                                qty: initialQty,
                                rate: suggestedRate > 0 ? suggestedRate : item.rate,
                                unit: existing.unit || (existing.itemType === "service" ? "Services" : "Nos"),
                                gstRate: existing.gstRate !== undefined ? Number(existing.gstRate) : item.gstRate,
                                isAutoCreated: false,
                              });
                            } else {
                              // 3. Genuinely new item -> automatically create in catalogue
                              const isPurchase = type === "Purchase";
                              const newItem = addStockItem({
                                name: sel.name.trim(),
                                itemType: sel.itemType || item.itemType || "goods",
                                stockGroup: sel.itemType === "service" ? "Services" : "General",
                                unit: item.unit || (sel.itemType === "service" ? "Services" : "Nos"),
                                hsnCode: sel.itemType === "service" ? "" : (item.hsnCode || ""),
                                sacCode: sel.itemType === "service" ? (item.sacCode || item.hsnCode || "") : "",
                                billingMethod: sel.itemType === "service" ? "one-time" : undefined,
                                standardRate: !isPurchase ? (Number(item.rate) || undefined) : undefined,
                                costRate: Number(item.rate) || 0,
                                currentRate: Number(item.rate) || 0,
                                lastPurchaseRate: isPurchase ? Number(item.rate) : null,
                                lastSellingRate: !isPurchase ? Number(item.rate) : null,
                                sellingRateVerified: !isPurchase,
                                gstRate: item.gstRate !== undefined ? Number(item.gstRate) : 18,
                                openingQty: 0,
                                openingRate: Number(item.rate) || 0,
                                openingValue: 0,
                                currentQty: 0,
                                currentValue: 0,
                                status: "active",
                              });

                              updateItemFields(item.id, {
                                itemId: newItem.id,
                                description: sel.name.trim(),
                                itemType: newItem.itemType,
                                hsnCode: newItem.hsnCode || "",
                                sacCode: newItem.sacCode || "",
                                qty: initialQty,
                                rate: item.rate,
                                unit: newItem.unit,
                                gstRate: newItem.gstRate,
                                isAutoCreated: true,
                              });
                            }
                          }
                          focusRowField(item.id, "hsn");
                        }}
                        onAdvanceNext={() => focusRowField(item.id, "hsn")}
                        onEmptyRowEnter={() => handleEmptyRowEnter(item.id)}
                        placeholder="Type or select goods or services..."
                      />
                    </div>

                    {/* HSN / SAC Code */}
                    <div>
                      <input
                        ref={(el) => setFieldRef(item.id, "hsn", el)}
                        type="text"
                        value={item.hsnCode}
                        onChange={(e) => updateItemFields(item.id, { hsnCode: e.target.value })}
                        onKeyDown={(e) => handleFieldEnter(e, item.id, "hsn")}
                        className="h-9 w-full px-2 text-xs rounded-md border border-border bg-card outline-none focus:ring-1 focus:ring-accent font-mono"
                        placeholder={item.itemType === "service" ? "SAC Code" : "HSN Code"}
                        data-field="hsn"
                      />
                    </div>

                    {/* Quantity */}
                    <div>
                      {(() => {
                        const maxStock = getItemMaxStock(item);
                        const isQtyExceeded = type === "Sales" && item.itemType !== "service" && isFinite(maxStock) && item.qty > maxStock;
                        return (
                          <div className="relative">
                            <input
                              ref={(el) => setFieldRef(item.id, "qty", el)}
                              type="number"
                              min="0.01"
                              max={type === "Sales" && item.itemType !== "service" && isFinite(maxStock) ? maxStock : undefined}
                              step="any"
                              value={item.qty === 0 ? "" : item.qty}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                updateItemFields(item.id, { qty: val });
                                if (type === "Sales" && item.itemType !== "service" && isFinite(maxStock) && val > maxStock) {
                                  toast({
                                    title: "Stock Exceeded",
                                    description: `Max available stock for "${item.description || 'this item'}" is ${maxStock}.`,
                                    variant: "destructive",
                                  });
                                }
                              }}
                              onKeyDown={(e) => handleFieldEnter(e, item.id, "qty")}
                              className={`h-9 w-full px-2 text-xs text-right rounded-md border bg-card outline-none focus:ring-1 font-mono transition-colors ${
                                isQtyExceeded
                                  ? "border-destructive text-destructive focus:ring-destructive bg-destructive/10 font-bold"
                                  : "border-border focus:ring-accent"
                              }`}
                              data-field="qty"
                              placeholder="1"
                            />
                            {type === "Sales" && item.itemType !== "service" && isFinite(maxStock) && (
                              <span
                                className={`text-[10px] font-mono block text-right pr-1 pt-0.5 ${
                                  isQtyExceeded ? "text-destructive font-bold" : "text-muted-foreground"
                                }`}
                              >
                                Max: {maxStock}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Rate */}
                    <div>
                      <input
                        ref={(el) => setFieldRef(item.id, "rate", el)}
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.rate === 0 ? "" : item.rate}
                        onChange={(e) => updateItemFields(item.id, { rate: parseFloat(e.target.value) || 0 })}
                        onKeyDown={(e) => handleFieldEnter(e, item.id, "rate")}
                        className="h-9 w-full px-2 text-xs text-right rounded-md border border-border bg-card outline-none focus:ring-1 focus:ring-accent font-mono"
                        data-field="rate"
                        placeholder="0.00"
                      />
                    </div>

                    {/* Discount (₹) */}
                    <div>
                      <input
                        ref={(el) => setFieldRef(item.id, "discount", el)}
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.discount === 0 ? "" : item.discount}
                        onChange={(e) => updateItemFields(item.id, { discount: parseFloat(e.target.value) || 0 })}
                        onKeyDown={(e) => handleFieldEnter(e, item.id, "discount")}
                        className="h-9 w-full px-2 text-xs text-right rounded-md border border-border bg-card outline-none focus:ring-1 focus:ring-accent font-mono"
                        data-field="discount"
                        placeholder="0.00"
                      />
                    </div>

                    {/* GST Rate */}
                    <div data-field="gst">
                      <Select
                        value={String(item.gstRate)}
                        onValueChange={(v) => {
                          updateItemFields(item.id, { gstRate: Number(v) });
                          if (fastEntry) {
                            setTimeout(() => handleRowComplete(item.id), 80);
                          }
                        }}
                      >
                        <SelectTrigger
                          ref={(el) => setFieldRef(item.id, "gst", el)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              e.stopPropagation();
                              handleRowComplete(item.id);
                            }
                          }}
                          className="h-9 text-xs"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {GST_RATES.map((r) => (
                            <SelectItem key={r} value={String(r)} className="text-xs">
                              {r}%
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Taxable Line Amount */}
                    <div className="h-9 px-2 text-xs font-mono font-bold text-right flex items-center justify-end text-foreground">
                      ₹{item.amount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>

                    {/* Remove Row Button */}
                    <button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      className="h-9 w-9 flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors disabled:opacity-30 disabled:pointer-events-none rounded-md hover:bg-muted"
                      disabled={items.length <= 1}
                      title="Remove item"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Mobile View */}
                  <div className="lg:hidden space-y-2.5 p-3 rounded-lg border border-border bg-card">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-medium text-muted-foreground uppercase">Item {idx + 1}</span>
                        <Badge variant="outline" className={`text-[10px] ${item.itemType === "service" ? "text-purple-600 bg-purple-500/10" : "text-blue-600 bg-blue-500/10"}`}>
                          {item.itemType === "service" ? "⚡ Service" : "📦 Goods"}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-foreground font-mono">
                          ₹{item.amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeItem(item.id)}
                          className="text-muted-foreground hover:text-destructive p-1 disabled:opacity-30"
                          disabled={items.length <= 1}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>

                    <ItemAutocomplete
                      value={item.description}
                      transactionType={type}
                      allocatedQuantities={getAllocatedQuantitiesExcludingRow(item.id)}
                      currentRowId={item.id}
                      onChange={(v) => updateItemFields(item.id, { description: v })}
                      onSelectItem={(sel: SelectedItemPayload) => {
                        const isService = sel.itemType === "service";
                        const effectiveRate = Number(sel.rate) || 0;
                        const code = isService ? sel.sacCode || sel.hsnCode || "" : sel.hsnCode || "";

                        // If Sales and physical goods, clamp initial quantity to remaining available stock
                        let initialQty = item.qty || 1;
                        if (type === "Sales" && !isService) {
                          const allocMap = getAllocatedQuantitiesExcludingRow(item.id);
                          const currentPersisted = sel.id
                            ? Number(stockItems.find((s) => s.id === sel.id)?.currentQty ?? sel.currentQty ?? 0)
                            : Number(sel.currentQty ?? 0);
                          const allocatedOther = (sel.id && allocMap[sel.id]) || allocMap[sel.name.trim().toLowerCase()] || 0;
                          const availableStock = Math.max(0, currentPersisted - allocatedOther);
                          if (availableStock > 0 && initialQty > availableStock) {
                            initialQty = availableStock;
                          }
                        }

                        // 1. Existing catalogue item selected
                        if (sel.id) {
                          updateItemFields(item.id, {
                            itemId: sel.id,
                            description: sel.name,
                            itemType: sel.itemType,
                            hsnCode: code,
                            sacCode: sel.sacCode || "",
                            qty: initialQty,
                            rate: effectiveRate > 0 ? effectiveRate : item.rate,
                            unit: sel.unit || (isService ? "Services" : "Nos"),
                            gstRate: sel.gstRate !== undefined ? Number(sel.gstRate) : item.gstRate,
                            isAutoCreated: false,
                          });
                        } else {
                          // 2. Check if item already exists in catalogue by name
                          const existing = stockItems.find(
                            (si) => si.name.trim().toLowerCase() === sel.name.trim().toLowerCase()
                          );
                          if (existing) {
                            const isPurchase = type === "Purchase";
                            const suggestedRate = isPurchase
                              ? Number(existing.lastPurchaseRate || existing.costRate || existing.currentRate || 0)
                              : Number(existing.lastSellingRate || (existing.sellingRateVerified ? existing.standardRate : null) || 0);

                            updateItemFields(item.id, {
                              itemId: existing.id,
                              description: existing.name,
                              itemType: existing.itemType,
                              hsnCode: existing.hsnCode || existing.sacCode || "",
                              sacCode: existing.sacCode || "",
                              qty: initialQty,
                              rate: suggestedRate > 0 ? suggestedRate : item.rate,
                              unit: existing.unit || (existing.itemType === "service" ? "Services" : "Nos"),
                              gstRate: existing.gstRate !== undefined ? Number(existing.gstRate) : item.gstRate,
                              isAutoCreated: false,
                            });
                          } else {
                            // 3. Genuinely new item -> automatically create in catalogue
                            const isPurchase = type === "Purchase";
                            const newItem = addStockItem({
                              name: sel.name.trim(),
                              itemType: sel.itemType || item.itemType || "goods",
                              stockGroup: sel.itemType === "service" ? "Services" : "General",
                              unit: item.unit || (sel.itemType === "service" ? "Services" : "Nos"),
                              hsnCode: sel.itemType === "service" ? "" : (item.hsnCode || ""),
                              sacCode: sel.itemType === "service" ? (item.sacCode || item.hsnCode || "") : "",
                              billingMethod: sel.itemType === "service" ? "one-time" : undefined,
                              standardRate: !isPurchase ? (Number(item.rate) || undefined) : undefined,
                              costRate: Number(item.rate) || 0,
                              currentRate: Number(item.rate) || 0,
                              lastPurchaseRate: isPurchase ? Number(item.rate) : null,
                              lastSellingRate: !isPurchase ? Number(item.rate) : null,
                              sellingRateVerified: !isPurchase,
                              gstRate: item.gstRate !== undefined ? Number(item.gstRate) : 18,
                              openingQty: 0,
                              openingRate: Number(item.rate) || 0,
                              openingValue: 0,
                              currentQty: 0,
                              currentValue: 0,
                              status: "active",
                            });

                            updateItemFields(item.id, {
                              itemId: newItem.id,
                              description: sel.name.trim(),
                              itemType: newItem.itemType,
                              hsnCode: newItem.hsnCode || "",
                              sacCode: newItem.sacCode || "",
                              qty: initialQty,
                              rate: item.rate,
                              unit: newItem.unit,
                              gstRate: newItem.gstRate,
                              isAutoCreated: true,
                            });
                          }
                        }
                        focusRowField(item.id, "hsn");
                      }}
                      onAdvanceNext={() => focusRowField(item.id, "hsn")}
                      onEmptyRowEnter={() => handleEmptyRowEnter(item.id)}
                      placeholder="Search goods or services..."
                      className="h-10 text-xs"
                    />

                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-[10px] text-muted-foreground block">Qty</label>
                          {type === "Sales" && item.itemType !== "service" && isFinite(getItemMaxStock(item)) && (
                            <span className="text-[9px] font-mono text-muted-foreground">
                              Max: {getItemMaxStock(item)}
                            </span>
                          )}
                        </div>
                        {(() => {
                          const maxStock = getItemMaxStock(item);
                          const isQtyExceeded = type === "Sales" && item.itemType !== "service" && isFinite(maxStock) && item.qty > maxStock;
                          return (
                            <input
                              type="number"
                              min="0.01"
                              max={type === "Sales" && item.itemType !== "service" && isFinite(maxStock) ? maxStock : undefined}
                              value={item.qty === 0 ? "" : item.qty}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                updateItemFields(item.id, { qty: val });
                                if (type === "Sales" && item.itemType !== "service" && isFinite(maxStock) && val > maxStock) {
                                  toast({
                                    title: "Stock Exceeded",
                                    description: `Max available stock for "${item.description || 'this item'}" is ${maxStock}.`,
                                    variant: "destructive",
                                  });
                                }
                              }}
                              className={`h-8 w-full px-2 text-xs rounded border bg-card font-mono ${
                                isQtyExceeded ? "border-destructive text-destructive font-bold bg-destructive/10" : "border-border"
                              }`}
                            />
                          );
                        })()}
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground mb-1 block">Rate (₹)</label>
                        <input
                          type="number"
                          value={item.rate === 0 ? "" : item.rate}
                          onChange={(e) => updateItemFields(item.id, { rate: parseFloat(e.target.value) || 0 })}
                          className="h-8 w-full px-2 text-xs rounded border border-border bg-card font-mono"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground mb-1 block">Discount (₹)</label>
                        <input
                          type="number"
                          value={item.discount === 0 ? "" : item.discount}
                          onChange={(e) => updateItemFields(item.id, { discount: parseFloat(e.target.value) || 0 })}
                          className="h-8 w-full px-2 text-xs rounded border border-border bg-card font-mono"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              <div className="flex items-center justify-between pt-1">
                <button
                  type="button"
                  onClick={addItemAndFocus}
                  className="text-xs text-accent hover:underline flex items-center gap-1 font-semibold"
                >
                  <Plus className="h-3.5 w-3.5" /> + Add Another Item Line
                  {fastEntry && (
                    <kbd className="ml-1 text-[10px] px-1 py-0.5 rounded bg-muted text-muted-foreground border border-border font-mono">
                      Alt+A
                    </kbd>
                  )}
                </button>
                <span className="text-xs text-muted-foreground">
                  {items.length} item{items.length > 1 ? "s" : ""} · Gross: ₹{totals.grossTotal.toLocaleString("en-IN")}
                </span>
              </div>
            </div>

            {/* Section 5: Financial Summary & Tax Breakdown */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-2xs">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between py-1 border-b border-border/30">
                    <span className="text-muted-foreground">Gross Amount:</span>
                    <span className="font-mono font-medium">₹{totals.grossTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                  </div>
                  {totals.totalDiscount > 0 && (
                    <div className="flex justify-between py-1 border-b border-border/30 text-emerald-600 dark:text-emerald-400 font-medium">
                      <span>Total Discount:</span>
                      <span className="font-mono">- ₹{totals.totalDiscount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-1 font-semibold">
                    <span className="text-muted-foreground">Taxable Value:</span>
                    <span className="font-mono text-foreground">₹{totals.taxableTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>

                <div className="space-y-2 text-xs md:border-l border-border/50 md:pl-4">
                  {!isInterState ? (
                    <>
                      <div className="flex justify-between py-1 border-b border-border/30">
                        <span className="text-muted-foreground">Central GST (CGST):</span>
                        <span className="font-mono font-medium">₹{totals.cgstTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-border/30">
                        <span className="text-muted-foreground">State GST (SGST):</span>
                        <span className="font-mono font-medium">₹{totals.sgstTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between py-1 border-b border-border/30">
                      <span className="text-muted-foreground">Integrated GST (IGST):</span>
                      <span className="font-mono font-medium">₹{totals.igstTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-2 border-t border-border/70 font-bold">
                    <span className="text-foreground text-xs uppercase tracking-wider">Total Invoice Value:</span>
                    <span className="font-mono text-lg text-primary font-extrabold">
                      ₹{totals.grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 6: Unified Payment Settlement Section */}
            <div className="p-4 sm:p-5 rounded-xl border border-primary/20 dark:border-primary/30 bg-primary/[0.02] dark:bg-primary/[0.04] space-y-3.5 shadow-2xs">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 border-b border-border/60 pb-2.5">
                <div className="flex items-center gap-2">
                  <Wallet className="h-4 w-4 text-primary" />
                  <span className="text-xs font-bold uppercase tracking-wider text-foreground">
                    Payment Settlement
                  </span>
                  <span className="text-[11px] text-muted-foreground hidden sm:inline">
                    — Record settlement in one click without extra steps
                  </span>
                </div>

                {/* Settlement Mode Segmented Selector */}
                <div className="flex items-center gap-1 p-0.5 rounded-lg bg-muted/70 border border-border text-xs">
                  <button
                    type="button"
                    onClick={() => setSettlementMode("credit")}
                    className={`px-3 py-1 rounded-md font-medium transition-all ${
                      settlementMode === "credit"
                        ? "bg-card text-foreground shadow-xs font-semibold"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    Credit (Unpaid)
                  </button>
                  <button
                    type="button"
                    onClick={() => setSettlementMode("full")}
                    className={`px-3 py-1 rounded-md font-medium transition-all ${
                      settlementMode === "full"
                        ? "bg-emerald-600 text-white shadow-xs font-semibold"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    Fully Paid
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSettlementMode("partial");
                      if (paidAmount === 0 || paidAmount >= totals.grandTotal) {
                        setPaidAmount(Math.round(totals.grandTotal * 0.5 * 100) / 100);
                      }
                    }}
                    className={`px-3 py-1 rounded-md font-medium transition-all ${
                      settlementMode === "partial"
                        ? "bg-amber-600 text-white shadow-xs font-semibold"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    Partially Paid
                  </button>
                </div>
              </div>

              {settlementMode !== "credit" ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {/* Payment Method Selector */}
                    <div>
                      <label className="text-[11px] font-semibold text-muted-foreground uppercase block mb-1">
                        Payment Method
                      </label>
                      <Select
                        value={paymentMethod}
                        onValueChange={(val: PaymentMethod) => setPaymentMethod(val)}
                      >
                        <SelectTrigger className="h-9 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Bank Account" className="text-xs">🏦 Bank Transfer (NEFT/RTGS/IMPS)</SelectItem>
                          <SelectItem value="UPI" className="text-xs">📱 UPI / QR Code</SelectItem>
                          <SelectItem value="Cash" className="text-xs">💵 Cash</SelectItem>
                          <SelectItem value="Cheque" className="text-xs">📝 Cheque</SelectItem>
                          <SelectItem value="Card" className="text-xs">💳 Debit / Credit Card</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Target Bank/Cash Account Selector */}
                    <div>
                      <label className="text-[11px] font-semibold text-muted-foreground uppercase block mb-1">
                        Deposit / Withdrawal Account *
                      </label>
                      <Select
                        value={selectedAccountId}
                        onValueChange={setSelectedAccountId}
                      >
                        <SelectTrigger className="h-9 text-xs">
                          <SelectValue placeholder="Select Bank / Cash Ledger..." />
                        </SelectTrigger>
                        <SelectContent>
                          {bankAndCashLedgers.map((l) => (
                            <SelectItem key={l.id} value={l.id} className="text-xs">
                              <span className="font-medium">{l.name}</span>
                              <span className="text-[10px] text-muted-foreground ml-1.5">
                                (₹{l.currentBalance.toLocaleString("en-IN")} {l.currentBalanceType})
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Paid Amount */}
                    <div>
                      <label className="text-[11px] font-semibold text-muted-foreground uppercase block mb-1">
                        Amount Paid (₹) *
                      </label>
                      <input
                        type="number"
                        min="0.01"
                        max={totals.grandTotal}
                        step="0.01"
                        disabled={settlementMode === "full"}
                        value={paidAmount === 0 ? "" : paidAmount}
                        onChange={(e) => setPaidAmount(parseFloat(e.target.value) || 0)}
                        className={`h-9 w-full px-3 text-xs rounded-md border font-mono font-semibold outline-none focus:ring-1 focus:ring-primary ${
                          settlementMode === "full" ? "bg-muted text-foreground" : "bg-card border-border"
                        }`}
                        placeholder="0.00"
                      />
                    </div>

                    {/* UTR / Transaction Reference */}
                    <div>
                      <label className="text-[11px] font-semibold text-muted-foreground uppercase block mb-1">
                        Reference / UTR / Cheque No.
                      </label>
                      <input
                        type="text"
                        value={referenceNo}
                        onChange={(e) => setReferenceNo(e.target.value)}
                        placeholder="e.g. UTR-982144 or CHQ-0012"
                        className="h-9 w-full px-3 text-xs rounded-md border border-border bg-card outline-none focus:ring-1 focus:ring-primary font-mono"
                      />
                    </div>
                  </div>

                  {/* Settlement Balance Summary Badge */}
                  <div className="flex flex-wrap items-center justify-between gap-2 p-2.5 rounded-lg bg-muted/40 border border-border text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground font-medium">Status:</span>
                      <Badge
                        variant="outline"
                        className={
                          settlementMode === "full"
                            ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30"
                            : "bg-amber-500/10 text-amber-600 border-amber-500/30"
                        }
                      >
                        {settlementMode === "full" ? "✓ Fully Settled" : "⏳ Partial Payment"}
                      </Badge>
                      <span className="text-muted-foreground">
                        Paid: <strong className="text-foreground font-mono">₹{effectivePaid.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong>
                      </span>
                    </div>
                    <div className="text-muted-foreground">
                      Remaining on {isCustomerType ? "Customer Credit" : "Supplier Balance"}:{" "}
                      <strong className={`font-mono ${outstandingAmount > 0 ? "text-amber-600 font-bold" : "text-emerald-600"}`}>
                        ₹{outstandingAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </strong>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between p-2.5 rounded-lg bg-muted/40 border border-border text-xs text-muted-foreground">
                  <span>
                    No immediate payment. Full amount of <strong className="text-foreground font-mono">₹{totals.grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong> will be recorded as accounts {isCustomerType ? "receivable" : "payable"}.
                  </span>
                  <Badge variant="outline" className="text-muted-foreground">
                    Credit / Unpaid
                  </Badge>
                </div>
              )}
            </div>

            {/* Section 7: Narration */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-2xs space-y-2">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block flex items-center justify-between">
                <span>Narration / Remarks</span>
                <span className="text-[10px] text-muted-foreground font-normal">
                  Press Enter to Submit · Shift+Enter for newline
                </span>
              </label>
              <textarea
                ref={narrationRef}
                value={narration}
                onChange={(e) => setNarration(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSubmit("approve");
                  }
                }}
                className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-card outline-none focus:ring-1 focus:ring-primary resize-none font-mono"
                rows={2}
                placeholder="Enter invoice remarks or narration..."
              />
            </div>

            {/* Duplicate Entry Warning */}
            <DuplicateEntryWarning
              vouchers={vouchers}
              type={type}
              invoiceNumber={invoiceNumber}
              partyLedgerId={partyLedgerId}
              partyName={ledgers.find((l) => l.id === partyLedgerId)?.name}
              totalAmount={totals.grandTotal}
              narration={narration}
              date={invoiceDate}
            />

          </div>
        </FormModal>

        <AccountingRepairDialog
          open={showRepairDialog}
          onOpenChange={setShowRepairDialog}
          report={diagnosticReport}
          onRepaired={() => {
            setInlineRepairError(null);
          }}
        />

      {/* Immediate Invoice Preview / Print Modal */}
      {savedVoucherForPreview && (
        <InvoicePreviewDialog
          voucher={savedVoucherForPreview}
          open={previewOpen}
          onOpenChange={(isOpen) => {
            setPreviewOpen(isOpen);
            if (!isOpen) setSavedVoucherForPreview(null);
          }}
        />
      )}
    </>
  );
}
